/**
 * OrderReviewCard Component
 *
 * Displays a single review in an order context with:
 * - Reviewer information
 * - Overall rating (collapsed by default)
 * - Rating breakdown by criteria (expandable)
 * - Comment (expandable)
 * - Response thread (if exists)
 */

'use client';

import { FC, useState } from 'react';
import { useTranslations } from 'next-intl';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { Avatar } from '@/src/shared/ui/avatars/Avatar';
import { Stars } from '@/src/shared/ui/rating-stars/Stars';
import type { ReviewResponse, ContractorRatings, ClientRatings } from '@/src/shared/api/reviews.api';
import { cn } from '@/src/shared/lib/utils';

interface OrderReviewCardProps {
  review: ReviewResponse;
  viewerRole: 'client' | 'worker';
  isYourReview?: boolean; // Is this the viewer's own review?
  className?: string;
  defaultExpanded?: boolean;
  onEdit?: (reviewId: string) => void; // Callback for edit action
}

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Check if ratings are contractor ratings
 */
function isContractorRatings(ratings: any): ratings is ContractorRatings {
  return 'quality' in ratings && 'value' in ratings;
}

/**
 * OrderReviewCard - displays a single review with rating breakdown
 */
export const OrderReviewCard: FC<OrderReviewCardProps> = ({
  review,
  viewerRole,
  isYourReview,
  className,
  defaultExpanded = false,
  onEdit,
}) => {
  const t = useTranslations('reviews');
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Safety checks
  if (!review || !review.criteriaRatings) {
    return null;
  }

  const isContractor = isContractorRatings(review.criteriaRatings);
  const criteriaRatings = review.criteriaRatings as ContractorRatings | ClientRatings;
  const averageRating = review.averageRating || 0;

  // The API decides whether the review deadline is still open
  const canEdit = isYourReview && review.canEdit && onEdit;

  // Determine title based on viewer role
  const getTitle = () => {
    if (isYourReview) {
      return t('orderReview.yourReview');
    }

    // Other person's review
    if (viewerRole === 'client') {
      // Client viewing worker's review
      return t('orderReview.workerReview');
    } else {
      // Worker viewing client's review
      return t('orderReview.clientReview');
    }
  };

  return (
    <div className={cn('flex flex-col bg-background-primary rounded-[20px] border border-accent-secondary', className)}>
      {/* Compact Header - Always visible */}
      <div
        className="flex items-center justify-between p-4 w-full hover:bg-accent-secondary/10 transition-colors rounded-[20px] cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <Avatar
            src={review.reviewerActor?.avatarUrl}
            alt={review.reviewerActor?.displayName || review.reviewerActor?.name || 'User'}
            size="sm"
            fallback={(review.reviewerActor?.name || 'U').charAt(0).toUpperCase()}
          />
          <div className="text-left">
            <Typography variant="body" weight="semibold" color="primary">
              {getTitle()}
            </Typography>
            <div className="flex items-center gap-2 mt-0.5">
              <Stars count={5} rating={averageRating} size="xs" />
              <Typography variant="bodySm" weight="medium" color="primary">
                {averageRating.toFixed(1)}
              </Typography>
              <Typography variant="bodySm" color="tertiary">
                • {review.createdAt ? formatDate(review.createdAt) : 'N/A'}
              </Typography>
              {review.isEdited && (
                <Typography variant="bodySm" color="tertiary" className="italic">
                  • Edited
                </Typography>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Edit button - show if review can be edited */}
          {canEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(review.id);
              }}
              className="text-xs text-accent-primary hover:text-accent-primary/80 underline px-2"
            >
              Edit
            </button>
          )}

          {/* Expand/Collapse Icon */}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-text-tertiary flex-shrink-0" />
          ) : (
            <ChevronDown className="w-5 h-5 text-text-tertiary flex-shrink-0" />
          )}
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-0 flex flex-col gap-4">
          {/* Divider */}
          <div className="w-full h-px bg-accent-secondary" />

      {/* Criteria breakdown */}
      <div className="flex flex-col gap-3">
        <Typography variant="bodySm" weight="medium" color="secondary">
          {t('orderReview.ratingBreakdown')}
        </Typography>

        <div className="grid grid-cols-1 gap-2">
          {isContractor ? (
            <>
              {/* Contractor ratings */}
              <div className="flex items-center justify-between">
                <Typography variant="bodySm" color="secondary">
                  {t('criteria.quality')}
                </Typography>
                <div className="flex items-center gap-2">
                  <Stars count={5} rating={(criteriaRatings as ContractorRatings).quality || 0} size="xs" />
                  <Typography variant="bodySm" weight="medium" color="primary" className="w-6 text-right">
                    {(criteriaRatings as ContractorRatings).quality || 0}
                  </Typography>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Typography variant="bodySm" color="secondary">
                  {t('criteria.professionalism')}
                </Typography>
                <div className="flex items-center gap-2">
                  <Stars count={5} rating={(criteriaRatings as ContractorRatings).professionalism || 0} size="xs" />
                  <Typography variant="bodySm" weight="medium" color="primary" className="w-6 text-right">
                    {(criteriaRatings as ContractorRatings).professionalism || 0}
                  </Typography>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Typography variant="bodySm" color="secondary">
                  {t('criteria.communication')}
                </Typography>
                <div className="flex items-center gap-2">
                  <Stars count={5} rating={(criteriaRatings as ContractorRatings).communication || 0} size="xs" />
                  <Typography variant="bodySm" weight="medium" color="primary" className="w-6 text-right">
                    {(criteriaRatings as ContractorRatings).communication || 0}
                  </Typography>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Typography variant="bodySm" color="secondary">
                  {t('criteria.value')}
                </Typography>
                <div className="flex items-center gap-2">
                  <Stars count={5} rating={(criteriaRatings as ContractorRatings).value || 0} size="xs" />
                  <Typography variant="bodySm" weight="medium" color="primary" className="w-6 text-right">
                    {(criteriaRatings as ContractorRatings).value || 0}
                  </Typography>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Client ratings */}
              <div className="flex items-center justify-between">
                <Typography variant="bodySm" color="secondary">
                  {t('criteria.communication')}
                </Typography>
                <div className="flex items-center gap-2">
                  <Stars count={5} rating={(criteriaRatings as ClientRatings).communication || 0} size="xs" />
                  <Typography variant="bodySm" weight="medium" color="primary" className="w-6 text-right">
                    {(criteriaRatings as ClientRatings).communication || 0}
                  </Typography>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Typography variant="bodySm" color="secondary">
                  {t('criteria.professionalism')}
                </Typography>
                <div className="flex items-center gap-2">
                  <Stars count={5} rating={(criteriaRatings as ClientRatings).professionalism || 0} size="xs" />
                  <Typography variant="bodySm" weight="medium" color="primary" className="w-6 text-right">
                    {(criteriaRatings as ClientRatings).professionalism || 0}
                  </Typography>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Typography variant="bodySm" color="secondary">
                  {t('criteria.payment')}
                </Typography>
                <div className="flex items-center gap-2">
                  <Stars count={5} rating={(criteriaRatings as ClientRatings).payment || 0} size="xs" />
                  <Typography variant="bodySm" weight="medium" color="primary" className="w-6 text-right">
                    {(criteriaRatings as ClientRatings).payment || 0}
                  </Typography>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Comment */}
      {review.comment && (
        <>
          <div className="w-full h-px bg-accent-secondary" />
          <div>
            <Typography variant="bodySm" weight="medium" color="secondary" className="mb-2">
              {t('orderReview.comment')}
            </Typography>
            <Typography variant="body" color="primary">
              {review.comment}
            </Typography>
          </div>
        </>
      )}

      {/* Response */}
      {review.response && (
        <>
          <div className="w-full h-px bg-accent-secondary" />
          <div className="bg-background-secondary p-3 rounded-lg">
            <Typography variant="bodySm" weight="semibold" color="primary" className="mb-2">
              {t('orderReview.response')}
            </Typography>
            <Typography variant="bodySm" color="secondary">
              {review.response.content}
            </Typography>
            <Typography variant="bodySm" color="tertiary" className="mt-2">
              {formatDate(review.response.createdAt)}
            </Typography>
          </div>
        </>
      )}
        </div>
      )}
    </div>
  );
};
